<template>
  <v-toolbar
    density="compact"
    color="primary-lighten-1"
    class="v-toolbar-grow"
  >
    <div class="toolbar-items">
      <v-speed-dial
        location="bottom center"
        transition="fade-transition"
      >
        <template #activator="{ props: activatorProps }">
          <v-btn
            color="primary-darken-1"
            v-bind="activatorProps"
            :size="size"
            rounded="0"
            :icon="isHeading4 || isHeading5 || isHeading6 ? 'fas fa-heading' : 'fas fa-paragraph'"
            :active="isParagraph || isHeading4 || isHeading5 || isHeading6"
            :aria-label="t('html_editor.text_formatting')"
          />
        </template>
        <v-btn
          key="1"
          v-tooltip="t('html_editor.paragraph')"
          icon="fas fa-paragraph"
          :active="isParagraph"
          :disabled="disabled"
          elevation="5"
          color="font"
          :size="size"
          :aria-label="t('html_editor.paragraph')"
          @click="emit('setParagraph')"
        />
        <v-btn
          key="2"
          v-tooltip="t('html_editor.heading_large')"
          icon
          :active="isHeading4"
          :disabled="disabled"
          elevation="5"
          color="primary-darken-1"
          :size="size"
          :aria-label="t('html_editor.heading_large')"
          @click="emit('setHeading', 4)"
        >
          <v-icon>
            <i class="fas fa-heading mr-1" />
            <i class="fas fa-1 fa-2xs" />
          </v-icon>
        </v-btn>
        <v-btn
          key="3"
          v-tooltip="t('html_editor.heading_medium')"
          icon
          :active="isHeading5"
          :disabled="disabled"
          elevation="5"
          color="primary"
          :size="size"
          :aria-label="t('html_editor.heading_medium')"
          @click="emit('setHeading', 5)"
        >
          <v-icon>
            <i class="fas fa-heading mr-1" />
            <i class="fas fa-2 fa-2xs" />
          </v-icon>
        </v-btn>
        <v-btn
          key="4"
          v-tooltip="t('html_editor.heading_small')"
          icon
          :active="isHeading6"
          :disabled="disabled"
          elevation="5"
          color="primary-darken-1"
          :size="size"
          :aria-label="t('html_editor.heading_small')"
          @click="emit('setHeading', 6)"
        >
          <v-icon>
            <i class="fas fa-heading mr-1" />
            <i class="fas fa-3 fa-2xs" />
          </v-icon>
        </v-btn>
      </v-speed-dial>
      <v-divider
        class="ma-2"
        color="primary"
        vertical
        inset
      />
      <v-speed-dial
        location="bottom center"
        transition="fade-transition"
      >
        <template #activator="{ props: activatorProps }">
          <v-btn
            v-tooltip="t('html_editor.text_alignment')"
            v-bind="activatorProps"
            :size="size"
            rounded="0"
            color="primary-darken-1"
            :icon="`fas fa-align-${alignment || 'left'}`"
            :aria-label="t('html_editor.text_alignment')"
          />
        </template>
        <v-btn
          key="1"
          v-tooltip="t('html_editor.align_left')"
          icon="fas fa-align-left"
          color="primary-darken-1"
          :active="alignment === 'left'"
          :disabled="disabled"
          rounded="0"
          :size="size"
          :aria-label="t('html_editor.align_left')"
          @click="emit('toggleTextAlign', 'left')"
        />
        <v-btn
          key="2"
          v-tooltip="t('html_editor.align_center')"
          icon="fas fa-align-center"
          :active="alignment === 'center'"
          :disabled="disabled"
          rounded="0"
          color="primary-darken-1"
          :size="size"
          :aria-label="t('html_editor.align_center')"
          @click="emit('toggleTextAlign', 'center')"
        />
        <v-btn
          key="3"
          v-tooltip="t('html_editor.align_right')"
          icon="fas fa-align-right"
          :active="alignment === 'right'"
          :disabled="disabled"
          color="primary-darken-1"
          rounded="0"
          :size="size"
          :aria-label="t('html_editor.align_right')"
          @click="emit('toggleTextAlign', 'right')"
        />
        <v-btn
          key="4"
          v-tooltip="t('html_editor.align_justify')"
          icon="fas fa-align-justify"
          :active="alignment === 'justify'"
          :disabled="disabled"
          color="primary-darken-1"
          rounded="0"
          :size="size"
          :aria-label="t('html_editor.align_justify')"
          @click="emit('toggleTextAlign', 'justify')"
        />
      </v-speed-dial>
      <v-divider
        class="ma-2"
        color="primary"
        vertical
        inset
      />
      <v-btn
        v-tooltip="t('html_editor.list_bulleted')"
        icon="fas fa-list-ul"
        :active="isBulletList"
        :disabled="disabled"
        color="primary-darken-1"
        rounded="0"
        :size="size"
        :aria-label="t('html_editor.list_bulleted')"
        @click="emit('toggleBulletList')"
      />
      <v-btn
        v-tooltip="t('html_editor.list_ordered')"
        icon="fas fa-list-ol"
        :active="isOrderedList"
        :disabled="disabled"
        rounded="0"
        color="primary-darken-1"
        :size="size"
        :aria-label="t('html_editor.list_ordered')"
        @click="emit('toggleOrderedList')"
      />
      <v-divider
        class="ma-2"
        color="primary"
        vertical
        inset
      />

      <template v-if="isLink">
        <v-btn
          v-tooltip="t('html_editor.link_remove')"
          icon="fas fa-unlink"
          :disabled="!isLink"
          rounded="0"
          color="primary-darken-1"
          :size="size"
          :aria-label="t('html_editor.link_remove')"
          @click="emit('removeLink')"
        />
        <v-divider
          class="ma-2"
          color="primary"
          vertical
          inset
        />
      </template>
      <v-btn
        v-tooltip="t('html_editor.horizontal_line')"
        icon="fas fa-minus"
        :disabled="disabled"
        color="primary-darken-1"

        rounded="0"
        :size="size"
        :aria-label="t('html_editor.horizontal_line')"
        @click="emit('addHorizontalRule')"
      />
      <v-divider
        class="ma-2"
        color="primary"
        vertical
        inset
      />
      <v-speed-dial
        location="bottom center"
        transition="fade-transition"
      >
        <template #activator="{ props: activatorProps }">
          <v-btn
            v-tooltip="t('html_editor.tables')"
            v-bind="activatorProps"
            :size="size"
            rounded="0"
            color="primary-darken-1"

            icon="fas fa-table"
            :aria-label="t('html_editor.tables')"
            :active="isTable"
          />
        </template>
        <v-btn
          key="1"
          v-tooltip="t('html_editor.table_add')"
          :icon="tableIcons.tablePlus"

          color="primary-darken-1"

          elevation="5"
          :aria-label="t('html_editor.table_add')"
          @click="emit('insertTable')"
        />
        <v-btn
          key="2"
          v-tooltip="t('html_editor.table_remove')"
          :icon="tableIcons.tableRemove"
          color="error"

          elevation="5"
          :aria-label="t('html_editor.table_remove')"
          @click="emit('deleteTable')"
        />
        <v-btn
          v-if="isTable"
          key="3"
          v-tooltip="t('html_editor.table_column_add_before')"
          :icon="tableIcons.columnPlusBefore"

          elevation="5"
          color="primary-darken-1"

          :aria-label="t('html_editor.table_column_add_before')"
          @click="emit('addColumnBefore')"
        />
        <v-btn
          v-if="isTable"
          key="4"
          v-tooltip="t('html_editor.table_column_add_after')"
          :icon="tableIcons.columnPlusAfter"
          color="primary-darken-1"

          elevation="5"
          :aria-label="t('html_editor.table_column_add_after')"
          @click="emit('addColumnAfter')"
        />
        <v-btn
          v-if="isTable"
          key="5"
          v-tooltip="t('html_editor.table_column_remove')"
          :icon="tableIcons.columnRemove"
          color="error"
          elevation="5"
          :aria-label="t('html_editor.table_column_remove')"
          @click="emit('deleteColumn')"
        />
        <v-btn
          v-if="isTable"
          key="6"
          v-tooltip="t('html_editor.table_row_add_before')"
          :icon="tableIcons.rowPlusBefore"
          color="primary-darken-1"

          elevation="5"
          :aria-label="t('html_editor.table_row_add_before')"
          @click="emit('addRowBefore')"
        />
        <v-btn
          v-if="isTable"
          key="7"
          v-tooltip="t('html_editor.table_row_add_after')"
          :icon="tableIcons.rowPlusAfter"
          color="primary-darken-1"
          elevation="5"
          :aria-label="t('html_editor.table_row_add_after')"
          @click="emit('addRowAfter')"
        />
        <v-btn
          v-if="isTable"
          key="8"
          v-tooltip="t('html_editor.table_row_remove')"
          :icon="tableIcons.rowRemove"
          color="error"
          elevation="5"
          :aria-label="t('html_editor.table_row_remove')"
          @click="emit('deleteRow')"
        />
      </v-speed-dial>
      <v-divider
        class="ma-2"
        color="primary"
        vertical
        inset
      />
      <v-btn
        v-tooltip="t('html_editor.indent')"
        icon="fas fa-indent"
        :disabled="!canIndent || disabled"
        rounded="0"
        :size="size"
        color="primary-darken-1"

        :aria-label="t('html_editor.indent')"
        @click="emit('indent')"
      />
      <v-btn
        v-tooltip="t('html_editor.outdent')"
        icon="fas fa-outdent"
        :disabled="!canRemoveIndent || disabled"
        rounded="0"
        color="primary-darken-1"

        :size="size"
        :aria-label="t('html_editor.outdent')"
        @click="emit('outdent')"
      />
      <v-divider
        class="ma-2"
        color="primary"
        vertical
        inset
      />
      <v-btn
        v-tooltip="t('html_editor.undo')"
        icon="fas fa-undo"
        :disabled="!canUndo"
        rounded="0"
        :size="size"
        color="primary-darken-1"

        :aria-label="t('html_editor.undo')"
        @click="emit('undo')"
      />
      <v-btn
        v-tooltip="t('html_editor.redo')"
        icon="fas fa-redo"
        :disabled="!canRedo"
        rounded="0"
        color="primary-darken-1"

        :size="size"
        :aria-label="t('html_editor.redo')"
        @click="emit('redo')"
      />
      <v-divider
        class="ma-2"
        color="primary"
        vertical
        inset
      />
    </div>
  </v-toolbar>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';

import { tableIcons } from './tableIcons';
import { Level, TextAlignment } from './types';

defineProps({
  disabled: Boolean,
  isBold: Boolean,
  isItalic: Boolean,
  currentColor: String,
  isParagraph: Boolean,
  isHeading4: Boolean,
  isHeading5: Boolean,
  isHeading6: Boolean,
  isBulletList: Boolean,
  isOrderedList: Boolean,
  isLink: Boolean,
  isTable: Boolean,
  canUndo: Boolean,
  canRedo: Boolean,
  canIndent: Boolean,
  canRemoveIndent: Boolean,
  alignment: {
    type: String as () => TextAlignment | undefined,
  },
});
const emit = defineEmits<{
  (_e: 'setHeading', _level: Level): void;
  (_e: 'setParagraph'): void;
  (_e: 'toggleTextAlign', _alignment: TextAlignment): void;
  (_e: 'toggleBulletList'): void;
  (_e: 'toggleOrderedList'): void;
  (_e: 'removeLink'): void;
  (_e: 'addHorizontalRule'): void;
  (_e: 'insertTable'): void;
  (_e: 'deleteTable'): void;
  (_e: 'addColumnBefore'): void;
  (_e: 'addColumnAfter'): void;
  (_e: 'deleteColumn'): void;
  (_e: 'addRowBefore'): void;
  (_e: 'addRowAfter'): void;
  (_e: 'deleteRow'): void;
  (_e: 'indent'): void;
  (_e: 'outdent'): void;
  (_e: 'undo'): void;
  (_e: 'redo'): void;
}>();
const { t } = useI18n();

const { xs } = useDisplay();
const size = computed(() => (xs.value ? 'x-small' : 'small'));
</script>
